/**
 * Gradient Avatar Component
 *
 * Generates a unique colorful gradient avatar based on a string (e.g. the user's email)
 * Uses a simple hash function to ensure the same string always produces the same gradient
 */

interface GradientAvatarProps {
    identifier: string;
    size?: number;
    className?: string;
    alt?: string;
}

/**
 * MurmurHash3 - fast hash function with good avalanche properties
 * (small changes to input produce dramatically different outputs)
 */
function hashString(str: string): number {
    let hash = 0x811c9dc5; // FNV offset basis

    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);

        // FNV-1a algorithm - highly sensitive to input changes
        hash ^= char;
        hash = Math.imul(hash, 0x01000193); // FNV prime

        // Additional mixing for better avalanche
        hash ^= hash >>> 16;
        hash = Math.imul(hash, 0x85ebca6b);
        hash ^= hash >>> 13;
        hash = Math.imul(hash, 0xc2b2ae35);
        hash ^= hash >>> 16;
    }

    return Math.abs(hash);
}

/**
 * RAPIDS brand color palette for gradients
 * Using only the brighter colors from the brand palette for better visual appeal
 */
const BRAND_COLORS = [
    '#CBE880', // Primary light green
    '#DAA8E2', // Secondary light purple
    '#95D4E9', // Secondary light blue
    '#FFC27A', // Secondary light yellow
    '#BF5627', // Highlight orange (medium brightness but vibrant)
];

/**
 * Select brand colors from hash
 */
function selectBrandColors(hash: number): [string, string] {
    const index1 = hash % BRAND_COLORS.length;
    const index2 = (hash + 3) % BRAND_COLORS.length; // Offset to get different color

    // Ensure we don't pick the same color twice
    const finalIndex2 = index1 === index2 ? (index2 + 1) % BRAND_COLORS.length : index2;

    return [BRAND_COLORS[index1], BRAND_COLORS[finalIndex2]];
}

/**
 * Extract the first alphabetic character from a string (e.g., email)
 */
function getFirstAlphaChar(str: string): string {
    const match = str.match(/[a-zA-Z]/);
    return match ? match[0].toUpperCase() : '?';
}

export function GradientAvatar({identifier, size = 24, className = '', alt}: GradientAvatarProps) {
    const hash = hashString(identifier);

    // Generate two colors for the gradient from brand palette
    const [color1, color2] = selectBrandColors(hash);

    // Random gradient direction based on hash
    const directions = [
        'to bottom right',
        'to right',
        'to bottom',
        'to top right',
        '135deg',
        '45deg'
    ];
    const direction = directions[hash % directions.length];

    // Get first alphabetic character from identifier
    const initial = getFirstAlphaChar(identifier);

    return (
        <div
            className={`rounded-full flex items-center justify-center font-semibold text-white ${className}`}
            style={{
                width: size,
                height: size,
                background: `linear-gradient(${direction}, ${color1}, ${color2})`,
                fontSize: `${size * 0.5}px`,
            }}
            role="img"
            aria-label={alt || `Avatar for ${identifier}`}
        >
            {initial}
        </div>
    );
}
